/**
 * Loan Service — read-only queries for loan data.
 *
 * Loan-detail reads are served by the composite endpoint (getLoanComposite →
 * GET /loans/{id}?include=...); the old per-slice GET methods were removed with
 * their routes (loan-read consolidation, 2026-07-12). What remains here is the
 * base loan read, the composite, conditions, and the incomplete-applications
 * list.
 */

import { apiClient } from './api-client';
import type {
  LoanResult,
  OpenConditionsResult,
  IncompleteLoanSummary,
  LoanCompositeResult,
} from '@/types/loan';
import type { ConditionDetailsResult, LoanConditionsResult } from '@/types/condition';

export const loanService = {
  /** Full loan application data */
  getLoanApplication(loanId: string): Promise<LoanResult> {
    return apiClient.get<LoanResult>(`/api/v1/loans/${loanId}`);
  },

  /**
   * Composite loan read (API-MIGRATION-SPEC): base loan + requested detail/insight
   * sections in ONE trip. `include` keys are kebab-case section names (1:1 with the
   * former granular slice routes). The per-slice loan-detail hooks read through
   * this so the page makes one request instead of ~10.
   */
  getLoanComposite(loanId: string, include: readonly string[]): Promise<LoanCompositeResult> {
    const query = include.length ? `?include=${encodeURIComponent(include.join(','))}` : '';
    return apiClient.get<LoanCompositeResult>(`/api/v1/loans/${loanId}${query}`);
  },

  /** Open conditions list */
  getOpenConditions(loanId: string): Promise<OpenConditionsResult> {
    return apiClient.get<OpenConditionsResult>(`/api/v1/loans/${loanId}/conditions/open`);
  },

  /**
   * Borrower's in-progress applications.
   *
   * Adapter (API-MIGRATION-SPEC §2.2 #19): the API now returns a
   * `{ loans: LoanSummary[], count }` wrapper where LoanSummary is minimal
   * (`id, loanNumber, status, loanPurpose, currentStage, createdAt,
   * updatedAt` — no completionPercentage/propertyAddress/borrowerName).
   * Unwrap + map `id → loanId`; legacy array responses (mock mode / older
   * deployments) pass through unchanged. Resume = list item →
   * GET /application/{loanId} → hydrate.
   */
  async getIncompleteApplications(): Promise<IncompleteLoanSummary[]> {
    const raw = await apiClient.get<
      | IncompleteLoanSummary[]
      | { loans?: Array<Record<string, unknown>> | null; count?: number | string }
    >('/api/v1/loans/incomplete');

    if (Array.isArray(raw)) return raw; // legacy/mock shape — already loanId-keyed

    const loans = Array.isArray(raw?.loans) ? raw.loans : [];
    return loans.map((loan) => {
      const summary = loan as {
        id?: string;
        loanId?: string;
        loanNumber?: string | null;
        status?: string;
        loanPurpose?: string | null;
        currentStage?: string | null;
        createdAt?: string;
        updatedAt?: string;
      };
      return {
        loanId: summary.loanId ?? summary.id ?? '',
        status: summary.status,
        purpose: summary.loanPurpose ?? undefined,
        currentStage: summary.currentStage ?? undefined,
        loanNumber: summary.loanNumber ?? undefined,
        createdAt: summary.createdAt,
        updatedAt: summary.updatedAt,
      } satisfies IncompleteLoanSummary;
    }).filter((loan) => !!loan.loanId);
  },

  // 🟥 updateLoan (PATCH /api/v1/loans/{loanId}) REMOVED — endpoint deleted from
  // the API (Option A). Application edits go through PATCH /api/v1/application/{loanId}
  // (wizardService.update / useUpdateApplication) with delta payloads.
  //
  // 🟥 Per-slice reads (getTimeline/getSnapshot/getPricingAndLock/
  // getMonthlyHousingCosts/getFundsAtClosing/getLoanContacts/getNeedsAttention/
  // getHomeEquity/getPaymentHistory/getDisclosures) REMOVED — routes deleted;
  // loan-detail hooks read via getLoanComposite. getApplicationData
  // (GET /loans/{id}/application) REMOVED — route gone (2026-07-11).

  /** Every borrower-facing condition on the loan, as PATH returns it. */
  getLoanConditions(loanId: string): Promise<LoanConditionsResult> {
    return apiClient.get<LoanConditionsResult>(`/api/v1/loans/${loanId}/conditions`);
  },

  /** Detailed open conditions with instructions */
  getOpenConditionDetails(loanId: string): Promise<ConditionDetailsResult> {
    return apiClient.get<ConditionDetailsResult>(`/api/v1/loans/${loanId}/conditions`);
  },

  /**
   * NP-227: attach a purchase property to a submitted (pre-approval) loan.
   * POST creates, PUT updates an existing property. Endpoint is BACKEND-pending
   * (`POST/PUT /api/v1/loans/{loanId}/property`); the FE modal calls this and
   * surfaces failures until the API + downstream LO-task side effects land.
   */
  addProperty(loanId: string, payload: AddPropertyInput, update = false): Promise<AddPropertyResult> {
    const path = `/api/v1/loans/${loanId}/property`;
    return update
      ? apiClient.put<AddPropertyResult>(path, payload)
      : apiClient.post<AddPropertyResult>(path, payload);
  },
};

/** NP-227 post-submission property payload (POST/PUT /loans/{loanId}/property). */
export interface AddPropertyInput {
  streetAddress: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  purchasePrice: number;
  estimatedClosingDate: string; // yyyy-MM-dd
}

export interface AddPropertyResult {
  loanId?: string;
  status?: string;
}
